import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Modal from '../ui/Modal';
import { Button } from '../ui/Button';
import { toast } from '../ui/Toast';
import { invitationsApi } from '../../api/invitations.api';
import { useAuthStore } from '../../stores/auth.store';

interface Props {
  open: boolean;
  onClose: () => void;
}

const ERROR_KEYS: Record<string, string> = {
  INVITATION_CODE_NOT_FOUND: 'redeemCode.errors.notFound',
  EMAIL_NOT_VERIFIED: 'redeemCode.errors.emailNotVerified',
  INVITATION_EXPIRED: 'redeemCode.errors.expired',
  INVITATION_REVOKED: 'redeemCode.errors.revoked',
};

function errorKeyOf(err: unknown): string {
  const response = (err as { response?: { status?: number; data?: { error?: string } } })?.response;
  if (response?.status === 429) return 'redeemCode.errors.tooMany';
  return ERROR_KEYS[response?.data?.error ?? ''] ?? 'redeemCode.errors.generic';
}

/**
 * ADR-0033: the way into a trip an agency prepared. The code only works for
 * the email the agency has, so the modal says which one you are signed in with
 * — the usual reason a valid code is refused.
 */
export default function RedeemCodeModal({ open, onClose }: Props) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const email = useAuthStore(s => s.user?.email ?? '');
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!code.trim() || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      const { tripId } = await invitationsApi.redeemCode(code);
      toast(t('redeemCode.joined'), 'success');
      onClose();
      navigate(`/trips/${tripId}/itinerary`);
    } catch (err) {
      setError(t(errorKeyOf(err), { email }));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal open={open} onClose={onClose} title={t('redeemCode.title')} size="sm">
      <form onSubmit={submit} className="space-y-4">
        <p className="text-sm text-slate-600 dark:text-slate-400">{t('redeemCode.body', { email })}</p>
        <div>
          <label className="label" htmlFor="redeem-code">{t('redeemCode.label')}</label>
          <input
            id="redeem-code" className="input font-mono tracking-[0.3em] uppercase text-center text-lg"
            value={code} onChange={e => setCode(e.target.value)} maxLength={20}
            autoComplete="one-time-code" autoCapitalize="characters" spellCheck={false} autoFocus
            placeholder="ABCD 2345"
          />
        </div>
        {error && <p role="alert" className="text-sm text-red-600 dark:text-red-400">{error}</p>}
        <Button type="submit" variant="cta" className="w-full" loading={submitting} disabled={!code.trim()}>
          {t('redeemCode.submit')}
        </Button>
      </form>
    </Modal>
  );
}
